// Form Handling - Validation, Errors, Persistence

import { config } from './config.js';
import { debounce, isValidEmail } from '../../shared/js/utils/index.js';

const FORM_DATA_KEY = 'reforge_form_data';

/**
 * Put a button into or out of loading state.
 * Shows a spinner + contextual label; preserves original DOM without innerHTML mutation.
 * @param {HTMLButtonElement} btn
 * @param {boolean} isLoading
 * @param {string} [loadingText='Loading...']
 */
export function setButtonLoading(btn, isLoading, loadingText = 'Loading...') {
    if (!btn) return;

    if (isLoading) {
        btn.style.minWidth = btn.offsetWidth + 'px';

        btn.childNodes.forEach(node => {
            if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
                const wrapper = document.createElement('span');
                wrapper.className = 'btn-text';
                node.parentNode.replaceChild(wrapper, node);
                wrapper.appendChild(node);
            }
        });

        const content = document.createElement('span');
        content.className = 'btn-loading-content';

        const spinner = document.createElement('span');
        spinner.className = 'btn-spinner';
        spinner.setAttribute('aria-hidden', 'true');

        const label = document.createElement('span');
        label.textContent = loadingText;

        content.appendChild(spinner);
        content.appendChild(label);
        btn.appendChild(content);

        btn.disabled = true;
        btn.classList.add('loading');
        btn.setAttribute('aria-busy', 'true');

    } else {
        const content = btn.querySelector('.btn-loading-content');
        if (content) content.remove();

        btn.style.minWidth = '';
        btn.disabled = false;
        btn.classList.remove('loading');
        btn.removeAttribute('aria-busy');
    }
}

/**
 * Show form error
 */
export function showFormError(inputId, errorId, message) {
    const input = document.getElementById(inputId);
    const errorEl = document.getElementById(errorId);
    if (input) {
        input.classList.add('error');
        input.setAttribute('aria-invalid', 'true');
    }
    if (errorEl) {
        errorEl.textContent = message;
    }
}

/**
 * Clear form error
 */
export function clearFormError(inputId, errorId) {
    const input = document.getElementById(inputId);
    const errorEl = document.getElementById(errorId);
    if (input) {
        input.classList.remove('error');
        input.removeAttribute('aria-invalid');
    }
    if (errorEl) {
        errorEl.textContent = '';
    }
}

/**
 * Safe localStorage JSON parsing helper
 */
function getStoredFormData() {
    try {
        return JSON.parse(localStorage.getItem(FORM_DATA_KEY) || '{}');
    } catch (e) {
        localStorage.removeItem(FORM_DATA_KEY);
        return {};
    }
}

/**
 * Save form data to localStorage
 */
export function saveFormData(formId) {
    const form = document.getElementById(formId);
    if (!form) return;

    const data = {};
    form.querySelectorAll('input').forEach(input => {
        if (input.type !== 'submit' && input.type !== 'password' && input.name) {
            data[input.name] = input.value;
        }
    });

    try {
        const stored = getStoredFormData();
        stored[formId] = data;
        localStorage.setItem(FORM_DATA_KEY, JSON.stringify(stored));
    } catch (e) {
        // localStorage might be full or unavailable
    }
}

/**
 * Load form data from localStorage
 */
export function loadFormData(formId) {
    const stored = getStoredFormData();
    const data = stored[formId];
    if (!data) return;

    const form = document.getElementById(formId);
    if (!form) return;

    Object.entries(data).forEach(([name, value]) => {
        const input = form.querySelector(`[name="${name}"]`);
        if (input && value) {
            input.value = value;
        }
    });
}

/**
 * Clear form data from localStorage
 */
export function clearFormData(formId) {
    try {
        const stored = getStoredFormData();
        delete stored[formId];
        localStorage.setItem(FORM_DATA_KEY, JSON.stringify(stored));
    } catch (e) {
        // Silently fail
    }
}

/**
 * Initialize form persistence
 */
export function initFormPersistence() {
    loadFormData('downloadForm');
    loadFormData('checkoutForm');

    ['downloadForm', 'checkoutForm'].forEach(formId => {
        const form = document.getElementById(formId);
        if (form) {
            const debouncedSave = debounce(() => saveFormData(formId), config.ui.debounceDelay);
            form.addEventListener('input', debouncedSave);
        }
    });
}

/* ==========================================================================
   Mobile Email Reminder Form
   ========================================================================== */

/**
 * Handle mobile reminder form submission
 * API-backed handler - sends reminder email through backend
 * @param {Event} event - Form submit event
 */
export async function handleMobileReminder(event) {
    event.preventDefault();

    const form = event.target;
    const emailInput = document.getElementById('mobileReminderEmail');
    const errorEl = document.getElementById('mobileReminderError');
    const submitBtn = form.querySelector('button[type="submit"]');

    if (!emailInput) return;

    const email = emailInput.value.trim();

    // Validate email
    if (!email || !isValidEmail(email)) {
        emailInput.classList.add('error');
        if (errorEl) errorEl.textContent = 'Please enter a valid email address';
        return;
    }

    // Clear any previous error
    emailInput.classList.remove('error');
    if (errorEl) errorEl.textContent = '';

    // Disable button and show loading state
    setButtonLoading(submitBtn, true, 'Sending...');

    try {
        const response = await fetch('/stripe/mobile-reminder', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email })
        });

        let payload = null;
        try {
            payload = await response.json();
        } catch (e) {
            // Keep generic fallback below
        }

        if (!response.ok || !payload?.success) {
            const message = payload?.error || payload?.message || 'Could not send reminder. Please try again.';
            throw new Error(message);
        }
        // Replace form with success message
        const formContainer = form.parentElement;
        form.remove();

        // Remove privacy note if it exists
        const privacyNote = formContainer.querySelector('.notice-privacy');
        if (privacyNote) privacyNote.remove();

        // Insert success message
        const successDiv = document.createElement('div');
        successDiv.className = 'mobile-reminder-success';
        successDiv.innerHTML = `
            <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
            </svg>
            <span>${payload.message || "We'll email you a download link shortly."}</span>
        `;
        formContainer.appendChild(successDiv);
    } catch (error) {
        if (errorEl) {
            errorEl.textContent = error?.message || 'Failed to send reminder. Please try again.';
        }
        emailInput.classList.add('error');
        setButtonLoading(submitBtn, false);
    }
}

/**
 * Initialize mobile reminder form
 */
export function initMobileReminderForm() {
    const form = document.getElementById('mobileReminderForm');
    if (!form) return;

    form.addEventListener('submit', handleMobileReminder);

    // Clear error on input
    const emailInput = document.getElementById('mobileReminderEmail');
    if (emailInput) {
        emailInput.addEventListener('input', () => {
            emailInput.classList.remove('error');
            const errorEl = document.getElementById('mobileReminderError');
            if (errorEl) errorEl.textContent = '';
        });
    }
}
